/**
 * 底图 / 叠加层切换纯逻辑（从 Dashboard 拆出）
 */

import { getStyleModeFromBasemapId, isMandatoryOverlay } from '../mapProviderSettings';

export const MANDATORY_OVERLAY_INFO = '国界线为必显图层，随底图自动加载并纠偏，不可关闭';

/** 左上角快捷按钮用的样式模式 id（streets / satellite / hybrid / outdoors / terrain） */
export function resolveMapStyleIdFromProvider(provider, basemapId) {
  if (provider?.styleMode) return provider.styleMode;
  return getStyleModeFromBasemapId(basemapId);
}

export function computeNextOverlayIds(currentIds = [], overlayId) {
  if (currentIds.includes(overlayId)) {
    return currentIds.filter((id) => id !== overlayId);
  }
  return [...currentIds, overlayId];
}

/**
 * @returns {{ ok: boolean, reason?: string, nextIds?: string[], info?: string }}
 */
export function validateOverlayToggle(overlayId, nextIds, settings, canUseProvider) {
  if (isMandatoryOverlay(overlayId)) {
    return { ok: false, info: MANDATORY_OVERLAY_INFO };
  }
  if (overlayId === 'overlay-flight-routes') {
    return { ok: false, info: '飞行航线功能预留，后续版本支持' };
  }
  if (!canUseProvider(overlayId, settings) && nextIds.includes(overlayId)) {
    return { ok: false, reason: 'unavailable' };
  }
  return { ok: true, nextIds };
}

/**
 * 切换底图（纯决策 + 应用，state 由调用方写入）
 */
export function switchDashboardBasemap(options = {}) {
  const {
    map,
    basemapId,
    settings,
    activeOverlayIds = [],
    catalog = [],
    applyBasemap,
    canUseProvider,
    getProviderHint,
    writeLastBasemapChoice,
    onBasemapSettled,
    prevBasemapId
  } = options;

  if (!map || !canUseProvider?.(basemapId, settings)) {
    return {
      ok: false,
      warning: getProviderHint?.(basemapId, settings) || '该底图不可用，请先配置所需 Key 或瓦片地址'
    };
  }

  const provider = catalog.find((item) => item.id === basemapId);
  applyBasemap?.(map, basemapId, activeOverlayIds, settings, {
    onSettled: onBasemapSettled,
    prevBasemapId
  });
  writeLastBasemapChoice?.(basemapId);

  return {
    ok: true,
    basemapId,
    styleId: resolveMapStyleIdFromProvider(provider, basemapId),
    provider
  };
}

/**
 * 切换注记叠加层
 */
export function toggleDashboardOverlay(options = {}) {
  const {
    overlayId,
    currentOverlayIds = [],
    currentBasemapId,
    settings,
    catalog = [],
    map,
    applyBasemap,
    applyOverlayLayers,
    canUseProvider,
    getProviderHint,
    writeLastOverlayChoice,
    onBasemapSettled
  } = options;

  const next = computeNextOverlayIds(currentOverlayIds, overlayId);
  const validated = validateOverlayToggle(overlayId, next, settings, canUseProvider);
  if (validated.info) {
    return { ok: false, info: validated.info };
  }
  if (!validated.ok) {
    return {
      ok: false,
      warning: getProviderHint?.(overlayId, settings) || '该注记层不可用'
    };
  }

  writeLastOverlayChoice?.(next);
  const provider = catalog.find((item) => item.id === currentBasemapId);
  if (map) {
    if (provider?.type === 'raster') {
      applyBasemap?.(map, currentBasemapId, next, settings, {
        onSettled: onBasemapSettled,
        prevBasemapId: currentBasemapId
      });
    } else {
      applyOverlayLayers?.(map, next, settings);
    }
  }

  return { ok: true, nextIds: next, provider };
}
